import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Palette, ColorLens, SmartButton, ViewDay, Assignment, Gavel,
  ToggleOn, Link as LinkIcon, Image, AccountBalanceWallet, Settings,
  Info, Menu as MenuIcon, Store, Quiz, CalendarMonth, FitnessCenter,
  CreditCard, Recommend, CreditScore, ConfirmationNumber, RocketLaunch, Code,
} from '@mui/icons-material';
import { get, set, cloneDeep } from 'lodash';
import { FieldDef } from './types';
import { fieldRegistry } from './fieldRegistry';
import { WriteButton, WriteIconButton } from '../ui';
import useWriteGuard from '../../context/useWriteGuard';

const ICON_MAP: Record<string, React.ReactNode> = {
  Palette: <Palette sx={{ fontSize: 20 }} />, ColorLens: <ColorLens sx={{ fontSize: 20 }} />,
  SmartButton: <SmartButton sx={{ fontSize: 20 }} />, ViewDay: <ViewDay sx={{ fontSize: 20 }} />,
  Assignment: <Assignment sx={{ fontSize: 20 }} />, Gavel: <Gavel sx={{ fontSize: 20 }} />,
  ToggleOn: <ToggleOn sx={{ fontSize: 20 }} />, Link: <LinkIcon sx={{ fontSize: 20 }} />,
  Image: <Image sx={{ fontSize: 20 }} />, AccountBalanceWallet: <AccountBalanceWallet sx={{ fontSize: 20 }} />,
  Settings: <Settings sx={{ fontSize: 20 }} />, Info: <Info sx={{ fontSize: 20 }} />,
  Menu: <MenuIcon sx={{ fontSize: 20 }} />, Store: <Store sx={{ fontSize: 20 }} />,
  Quiz: <Quiz sx={{ fontSize: 20 }} />, Wallet: <AccountBalanceWallet sx={{ fontSize: 20 }} />,
  CalendarMonth: <CalendarMonth sx={{ fontSize: 20 }} />, FitnessCenter: <FitnessCenter sx={{ fontSize: 20 }} />,
  CreditCard: <CreditCard sx={{ fontSize: 20 }} />, Recommend: <Recommend sx={{ fontSize: 20 }} />,
  CreditScore: <CreditScore sx={{ fontSize: 20 }} />, ConfirmationNumber: <ConfirmationNumber sx={{ fontSize: 20 }} />,
  RocketLaunch: <RocketLaunch sx={{ fontSize: 20 }} />, Code: <Code sx={{ fontSize: 20 }} />,
};

interface CategoryInfo { key: string; label: string; icon: string; description: string; }

interface Props {
  schema: FieldDef[];
  data: Record<string, unknown>;
  onChange: (updated: Record<string, unknown>) => void;
  categories?: CategoryInfo[];
  height?: string;
  /**
   * Seed the field search box (e.g. a term deep-linked from global search).
   * When it changes, the search is reseeded and the first matching category is
   * auto-selected so the matching attribute is shown immediately.
   */
  searchSeed?: string;
}

// Build categories from schema if not provided
function buildCategories(schema: FieldDef[]): CategoryInfo[] {
  const seen = new Set<string>();
  const result: CategoryInfo[] = [];
  for (const f of schema) {
    if (!seen.has(f.group)) {
      seen.add(f.group);
      result.push({ key: f.group, label: f.group, icon: 'Settings', description: '' });
    }
  }
  return result;
}

const FIELD_TYPES = ['text', 'number', 'toggle', 'color', 'url', 'select', 'textarea', 'json', 'array', 'date'] as const;

const SchemaFormPanel: React.FC<Props> = ({ schema, data, onChange, categories, height = '60vh', searchSeed }) => {
  const cats = useMemo(() => categories || buildCategories(schema), [categories, schema]);
  const [activeCategory, setActiveCategory] = useState(cats[0]?.key || '');
  const [catSearch, setCatSearch] = useState('');
  // Field deep-linked from global search: scrolled to + highlighted (NOT
  // filtered, so editing it doesn't make it drop out of a filtered list).
  const [highlightKey, setHighlightKey] = useState<string | null>(null);
  const centerRef = useRef<HTMLDivElement>(null);
  const appliedSeedRef = useRef<string | undefined>(undefined);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [newFieldKey, setNewFieldKey] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState<string>('text');

  const { readOnly } = useWriteGuard();

  const grouped = useMemo(() => {
    const map: Record<string, FieldDef[]> = {};
    for (const f of schema) { (map[f.group] = map[f.group] || []).push(f); }
    return map;
  }, [schema]);

  // A field matches the query if its label, key, OR current value contains it.
  // Value matching lets you find an attribute by its content (e.g. a
  // "copyright" string), not just by its name — important for deep-links from
  // global search, which matches the raw JSONB.
  const fieldMatches = useCallback((f: FieldDef, q: string) => {
    if (f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)) return true;
    const v = get(data, f.key);
    return v != null && typeof v !== 'object' && String(v).toLowerCase().includes(q);
  }, [data]);

  // When searching, match both category names AND individual fields.
  const isFieldSearch = catSearch.length >= 2;

  // Jump to the field matching a deep-link term: switch to its category,
  // highlight it, and scroll it into view. Applied ONCE per distinct seed
  // (guarded by a ref) so subsequent edits — which change `data` and thus
  // `fieldMatches` — don't re-run this and yank the scroll back to top.
  useEffect(() => {
    if (!searchSeed || appliedSeedRef.current === searchSeed) return;
    appliedSeedRef.current = searchSeed;
    const q = searchSeed.toLowerCase();
    const target = schema.find((f) => fieldMatches(f, q));
    if (!target) return;
    setActiveCategory(target.group);
    setHighlightKey(target.key);
    const t = setTimeout(() => {
      centerRef.current
        ?.querySelector(`[data-field-key="${target.key}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(t);
  }, [searchSeed, schema, fieldMatches]);

  const filteredCats = useMemo(() => {
    const valid = cats.filter((c) => grouped[c.key]?.length > 0);
    if (!catSearch) return valid;
    const q = catSearch.toLowerCase();
    if (!isFieldSearch) return valid.filter((c) => c.label.toLowerCase().includes(q));
    // Match categories by name OR by having matching fields
    return valid.filter((c) =>
      c.label.toLowerCase().includes(q) ||
      (grouped[c.key] || []).some((f) => fieldMatches(f, q))
    );
  }, [cats, grouped, catSearch, isFieldSearch, fieldMatches]);

  // Count matching fields per category (for search badge)
  const matchCountByCategory = useMemo(() => {
    if (!isFieldSearch) return {};
    const q = catSearch.toLowerCase();
    const counts: Record<string, number> = {};
    for (const cat of cats) {
      counts[cat.key] = (grouped[cat.key] || []).filter((f) => fieldMatches(f, q)).length;
    }
    return counts;
  }, [cats, grouped, catSearch, isFieldSearch, fieldMatches]);

  const activeFields = useMemo(() => {
    let fields = grouped[activeCategory] || [];

    // Filter fields by search query when searching
    if (isFieldSearch) {
      const q = catSearch.toLowerCase();
      fields = fields.filter((f) => fieldMatches(f, q));
    }

    const visible = fields.filter((f) => {
      if (!f.dependsOn) return true;
      return get(data, f.dependsOn.key) === f.dependsOn.value;
    });
    const subGroups: Record<string, FieldDef[]> = {};
    for (const f of visible) { (subGroups[f.subGroup || ''] = subGroups[f.subGroup || ''] || []).push(f); }
    return subGroups;
  }, [grouped, activeCategory, data, catSearch, isFieldSearch, fieldMatches]);

  const activeCat = cats.find((c) => c.key === activeCategory);

  const handleFieldChange = useCallback((key: string, value: unknown) => {
    const next = cloneDeep(data);
    set(next, key, value);
    onChange(next);
  }, [data, onChange]);

  const handleDeleteField = useCallback((key: string) => {
    const next = cloneDeep(data);
    // Handle dot-path keys: unset the leaf
    const parts = key.split('.');
    let obj: any = next;
    for (let i = 0; i < parts.length - 1; i++) {
      obj = obj?.[parts[i]];
      if (!obj) return;
    }
    delete obj[parts[parts.length - 1]];
    onChange(next);
  }, [data, onChange]);

  const addFieldKeyExists = useMemo(() => {
    if (!newFieldKey.trim()) return false;
    return get(data, newFieldKey.trim()) !== undefined || schema.some(f => f.key === newFieldKey.trim());
  }, [newFieldKey, data, schema]);

  const handleAddField = useCallback(() => {
    const key = newFieldKey.trim();
    if (!key) return;
    const next = cloneDeep(data);
    const defaultVal = newFieldType === 'toggle' ? false : newFieldType === 'number' ? 0 : newFieldType === 'json' ? {} : newFieldType === 'array' ? [] : '';
    set(next, key, defaultVal);
    onChange(next);
    setNewFieldKey('');
    setNewFieldLabel('');
    setNewFieldType('text');
    setAddFieldOpen(false);
  }, [data, onChange, newFieldKey, newFieldType]);

  const renderField = (field: FieldDef) => {
    const val = get(data, field.key);
    const isHighlighted = highlightKey === field.key;
    if (field.type === 'toggle') {
      const isOn = !!val;
      return (
        <Paper key={field.key} data-field-key={field.key} elevation={0} sx={{ p: 2, border: `1px solid ${isHighlighted ? '#F0C419' : '#EFF0F0'}`, backgroundColor: isHighlighted ? '#FFFBEF' : undefined, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', '&:hover .delete-field-btn': { opacity: 1 } }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#141417', fontFamily: 'Manrope, sans-serif' }}>{field.label}</Typography>
              <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: '#A1A1A1', backgroundColor: '#F5F5F5', px: 0.75, py: 0.15, borderRadius: '4px', lineHeight: 1.4 }}>{field.key}</Typography>
              {field.enables && field.enables.length > 0 && (
                <Chip label={`Enables ${field.enables.length} fields`} size="small" sx={{ fontSize: 10, fontWeight: 600, height: 20, backgroundColor: isOn ? '#FAB30620' : '#EFF0F0' }} />
              )}
            </Box>
            {field.description && <Typography sx={{ fontSize: 12, color: '#6F6F6F', lineHeight: 1.4 }}>{field.description}</Typography>}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Tooltip title={`Remove "${field.label}"`}>
              <WriteIconButton size="small" className="delete-field-btn"
                onClick={() => handleDeleteField(field.key)}
                sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25, color: '#DC2626', '&:hover': { backgroundColor: '#FEE2E2' } }}>
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </WriteIconButton>
            </Tooltip>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: isOn ? '#3E8C1C' : '#6F6F6F' }}>{isOn ? 'Enabled' : 'Disabled'}</Typography>
            <Switch size="small" checked={isOn} onChange={(e) => handleFieldChange(field.key, e.target.checked)} disabled={readOnly}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FAB306' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FAB306' } }} />
          </Box>
        </Paper>
      );
    }
    const Component = fieldRegistry[field.type];
    if (!Component) return null;
    const props: Record<string, unknown> = { label: field.label, description: field.description, value: val, onChange: (v: unknown) => handleFieldChange(field.key, v), placeholder: field.placeholder, allData: data, onFieldChange: handleFieldChange, menuKind: field.menuKind };
    if (field.type === 'select') props.options = field.options || [];
    if (field.type === 'textarea') props.multiline = true;
    return (
      <Paper key={field.key} data-field-key={field.key} elevation={0} sx={{ p: 2, border: `1px solid ${isHighlighted ? '#F0C419' : '#EFF0F0'}`, backgroundColor: isHighlighted ? '#FFFBEF' : undefined, borderRadius: '10px', position: 'relative', '&:hover .delete-field-btn': { opacity: 1 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: '#A1A1A1' }}>{field.key}</Typography>
          <Tooltip title={`Remove "${field.label}"`}>
            <WriteIconButton size="small" className="delete-field-btn"
              onClick={() => handleDeleteField(field.key)}
              sx={{ opacity: 0, transition: 'opacity 0.15s', p: 0.25, color: '#DC2626', '&:hover': { backgroundColor: '#FEE2E2' } }}>
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </WriteIconButton>
          </Tooltip>
        </Box>
        <Component {...props} />
      </Paper>
    );
  };

  return (
    <Box sx={{ display: 'flex', gap: 0, border: '1px solid #EFF0F0', borderRadius: '12px', overflow: 'hidden', minHeight: height, backgroundColor: '#FFFFFF' }}>
      {/* Left sidebar */}
      <Box sx={{ width: 240, flexShrink: 0, borderRight: '1px solid #EFF0F0', backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 1.5 }}>
          <TextField size="small" fullWidth placeholder="Search fields..." value={catSearch}
            onChange={(e) => {
              setCatSearch(e.target.value);
              // Auto-select first category with matching fields when searching
              if (e.target.value.length >= 2) {
                const q = e.target.value.toLowerCase();
                const match = cats.find((c) => (grouped[c.key] || []).some((f) => fieldMatches(f, q)));
                if (match) setActiveCategory(match.key);
              }
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#A1A1A1' }} /></InputAdornment>, sx: { fontSize: 12, borderRadius: '8px', backgroundColor: '#FFFFFF' } }} />
        </Box>
        <List sx={{ flex: 1, overflowY: 'auto', px: 1, py: 0 }}>
          {filteredCats.map((cat) => {
            const count = grouped[cat.key]?.length || 0;
            const isActive = activeCategory === cat.key;
            return (
              <ListItemButton key={cat.key} onClick={() => setActiveCategory(cat.key)}
                sx={{ borderRadius: '8px', mb: 0.5, py: 0.75, px: 1.5, backgroundColor: isActive ? '#FAB30615' : 'transparent', '&:hover': { backgroundColor: isActive ? '#FAB30620' : '#F0F0F0' } }}>
                <ListItemIcon sx={{ minWidth: 28, color: isActive ? '#FAB306' : '#6F6F6F' }}>
                  {ICON_MAP[cat.icon] || <Settings sx={{ fontSize: 20 }} />}
                </ListItemIcon>
                <ListItemText primary={<Typography sx={{ fontSize: 12, fontWeight: isActive ? 600 : 500, color: isActive ? '#141417' : '#4A546A' }}>{cat.label}</Typography>} />
                <Chip
                  label={isFieldSearch && matchCountByCategory[cat.key] ? `${matchCountByCategory[cat.key]} match${matchCountByCategory[cat.key] > 1 ? 'es' : ''}` : count}
                  size="small"
                  sx={{ height: 18, fontSize: 10, fontWeight: 600, backgroundColor: isFieldSearch && matchCountByCategory[cat.key] ? '#FAB30640' : isActive ? '#FAB30630' : '#E5E5E5' }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Center */}
      <Box ref={centerRef} sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {activeCat && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, fontFamily: 'Manrope, sans-serif', color: '#141417' }}>{activeCat.label}</Typography>
            {activeCat.description && <Typography sx={{ fontSize: 13, color: '#6F6F6F', mt: 0.5 }}>{activeCat.description}</Typography>}
          </Box>
        )}
        {Object.entries(activeFields).map(([subGroup, fields]) => (
          <Box key={subGroup} sx={{ mb: 3 }}>
            {subGroup && (
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#FAB306', textTransform: 'uppercase', letterSpacing: 1, mb: 1.5, fontFamily: 'Manrope, sans-serif' }}>{subGroup}</Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>{fields.map(renderField)}</Box>
          </Box>
        ))}
        {Object.keys(activeFields).length === 0 && (
          <Typography sx={{ textAlign: 'center', color: '#A1A1A1', py: 6, fontSize: 14 }}>No fields in this category.</Typography>
        )}
      </Box>

      {/* Right JSON (read-only) */}
      <Box sx={{ width: 280, flexShrink: 0, borderLeft: '1px solid #EFF0F0', backgroundColor: '#FAFAFA', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid #EFF0F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, fontFamily: 'Manrope, sans-serif' }}>JSON Preview</Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Add custom field">
              <WriteIconButton size="small" onClick={() => setAddFieldOpen(true)}
                sx={{ color: '#16A34A', '&:hover': { backgroundColor: '#DCFCE7' } }}>
                <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
              </WriteIconButton>
            </Tooltip>
            <Tooltip title="Copy JSON">
              <IconButton size="small" onClick={() => navigator.clipboard.writeText(JSON.stringify(data, null, 2))}
                sx={{ color: '#6F6F6F', '&:hover': { backgroundColor: '#EFF0F0' } }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
          <pre style={{ fontSize: 9, fontFamily: 'monospace', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#374151', lineHeight: 1.5 }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        </Box>
      </Box>

      {/* Add Field Dialog */}
      <Dialog open={addFieldOpen} onClose={() => setAddFieldOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: 'Manrope, sans-serif', fontSize: 16 }}>
          Add Custom Field
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField size="small" label="Field Key (dot-path)" placeholder="e.g. ux.newColor or myFlag"
              value={newFieldKey} onChange={(e) => setNewFieldKey(e.target.value)}
              error={addFieldKeyExists}
              helperText={addFieldKeyExists ? 'This key already exists — adding will overwrite the current value' : 'Use dots for nested keys: parent.child'}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }} />
            <TextField size="small" label="Display Label" placeholder="e.g. My New Color"
              value={newFieldLabel} onChange={(e) => setNewFieldLabel(e.target.value)} />
            <FormControl size="small">
              <InputLabel>Field Type</InputLabel>
              <Select value={newFieldType} label="Field Type" onChange={(e) => setNewFieldType(e.target.value)}>
                {FIELD_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddFieldOpen(false)}
            sx={{ textTransform: 'none', fontSize: 12, color: '#6F6F6F' }}>Cancel</Button>
          <WriteButton variant="contained" onClick={handleAddField} disabled={!newFieldKey.trim()}
            sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, backgroundColor: '#FAB306', color: '#141417',
              '&:hover': { backgroundColor: '#E0A005' }, borderRadius: '8px' }}>
            Add Field
          </WriteButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchemaFormPanel;
